/**
 * Package aggregate for the flexible-package configurator (B2B-only flow) — the objects exchanged
 * with the v2 packages API. Intentionally decoupled from the legacy `InsurancePackage` model / endpoints.
 *
 * Shape (matches the v2 API — no DTO/mapper layer):
 *  - `Package` — package core / metadata (`/v2/packages`) plus the effective-dated references to its
 *    coverages (`coverages[]` — coverage id + `activeFrom`).
 *  - `PackageContent` (наповнення) — one coverage (`/v2/packages/{id}/coverages/{coverageId}`). Pricing
 *    and the coverage (покриття) options both live here; every new content version IS a price revision.
 *    The API returns a coverage without its `activeFrom` — it comes from the core's `coverages[]`
 *    reference and is merged in by the service. Status is NOT stored — it is derived from the contents'
 *    `activeFrom` dates (see `resolveContentTimeline`).
 *  - `Pricing` (ціноутворення) — base policy cost, insured sum and age-group prices. The API stores each
 *    age group as a coefficient of the base cost; the mapper converts between the two.
 *
 * B2C-only fields (packageType, package-level dates) are intentionally omitted while the
 * flow is B2B-only.
 */
import { PackageClientType } from '../insurance-package.interface';
import { CoverageOption } from './coverage-option.model';

export interface AgeGroup {
  minAge: number;
  maxAge: number;
  /** Policy price for this age band, in грн; its coefficient is derived from the base policy cost. */
  price: number | null;
  /**
   * The coefficient as stored by the API. Shown while no price can be derived (no base cost yet), and used to
   * fill the price once a base cost is entered. Absent for groups added in the UI.
   */
  coefficient?: number | null;
}

export interface Pricing {
  /** Base policy cost (базова вартість полісу), in whole грн — the base the age-group coefficients are relative to. */
  basePolicyCost: number | null;
  /** Insured sum (страхова сума), in грн. */
  insuredSum: number | null;
  ageGroups: AgeGroup[];
}

/** A single effective-dated content version (наповнення) of a package — an API coverage. */
export interface PackageContent {
  id: string;
  /** ISO date the content becomes active; null only for a local draft (required by the API on save). */
  activeFrom: string | null;
  pricing: Pricing;
  /** Coverage (покриття) — the option/limit table for this content version. */
  options: CoverageOption[];
}

/** Effective-dated link from a package core to one of its coverages. */
export interface PackageCoverageReference {
  coverageId: string;
  activeFrom: string;
  removedAt?: string;
  removedBy?: string;
}

/** Package core (metadata) — the `/v2/packages` resource. */
export interface Package {
  id: string;
  name: string;
  internalName?: string;
  description: string;
  internalDescription?: string;
  /** URL to the full package description document, surfaced to the member. */
  packageDescriptionUrl?: string;
  clientType?: PackageClientType;
  /** References to the package's coverages (read-only — managed by the coverage endpoints). */
  coverages?: PackageCoverageReference[];
}

/** Derived content lifecycle state — computed from the active dates, never stored. */
export enum PackageContentStatus {
  Upcoming = 'Upcoming',
  Active = 'Active',
  Archived = 'Archived',
}

export interface PackageContentTimeline {
  status: PackageContentStatus;
  /** When an archived content stopped being active — the start of the content that replaced it. */
  activeTo: string | null;
}

/**
 * Resolves each content's status relative to `now`: the latest started content is active, earlier started ones
 * are archived (each until the next one's start) and future ones are upcoming. A content without a date (a local
 * draft) is upcoming next to an active content, otherwise it becomes the first active one.
 */
export function resolveContentTimeline(contents: Pick<PackageContent, 'id' | 'activeFrom'>[], now: Date = new Date()): Map<string, PackageContentTimeline> {
  const timeOf = (activeFrom: string): number => new Date(activeFrom).getTime();
  const dated = contents
    .filter((content): content is { id: string; activeFrom: string } => !!content.activeFrom)
    .sort((first, second) => timeOf(first.activeFrom) - timeOf(second.activeFrom));
  const active = dated.filter(content => timeOf(content.activeFrom) <= now.getTime()).at(-1);
  const timeline = new Map<string, PackageContentTimeline>();

  dated.forEach((content, index) => {
    if (timeOf(content.activeFrom) > now.getTime()) timeline.set(content.id, { status: PackageContentStatus.Upcoming, activeTo: null });
    else if (content === active) timeline.set(content.id, { status: PackageContentStatus.Active, activeTo: null });
    else timeline.set(content.id, { status: PackageContentStatus.Archived, activeTo: dated[index + 1].activeFrom });
  });
  contents
    .filter(content => !content.activeFrom)
    .forEach(content => timeline.set(content.id, { status: active ? PackageContentStatus.Upcoming : PackageContentStatus.Active, activeTo: null }));

  return timeline;
}

export const DEFAULT_AGE_GROUPS: AgeGroup[] = [
  { minAge: 0, maxAge: 1, price: null },
  { minAge: 2, maxAge: 6, price: null },
  { minAge: 7, maxAge: 17, price: null },
  { minAge: 18, maxAge: 64, price: null },
];

export function buildDefaultPricing(): Pricing {
  return { basePolicyCost: null, insuredSum: null, ageGroups: DEFAULT_AGE_GROUPS.map(group => ({ ...group })) };
}

/**
 * Indexes of age groups whose ranges share at least one age with another group (e.g. 18–50 and 50–65).
 * Groups with an inverted range are skipped — they are reported separately.
 */
export function findOverlappingAgeGroups(groups: AgeGroup[]): Set<number> {
  const overlapping = new Set<number>();
  const ordered = groups
    .map((group, index) => ({ group, index }))
    .filter(({ group }) => group.minAge <= group.maxAge)
    .sort((first, second) => first.group.minAge - second.group.minAge);
  let widest: { group: AgeGroup; index: number } | null = null;

  for (const current of ordered) {
    if (widest && current.group.minAge <= widest.group.maxAge) {
      overlapping.add(widest.index);
      overlapping.add(current.index);
    }
    if (!widest || current.group.maxAge > widest.group.maxAge) widest = current;
  }

  return overlapping;
}

/**
 * Age groups that leave uncovered ages before them (e.g. 18–50 followed by 55–65), mapped to the age they should
 * start at. Groups with an inverted range are skipped — they are reported separately.
 */
export function findAgeGroupGaps(groups: AgeGroup[]): Map<number, number> {
  const gaps = new Map<number, number>();
  const ordered = groups
    .map((group, index) => ({ group, index }))
    .filter(({ group }) => group.minAge <= group.maxAge)
    .sort((first, second) => first.group.minAge - second.group.minAge);
  let coveredTo: number | null = null;

  for (const { group, index } of ordered) {
    if (coveredTo !== null && group.minAge > coveredTo + 1) gaps.set(index, coveredTo + 1);
    coveredTo = Math.max(coveredTo ?? group.maxAge, group.maxAge);
  }

  return gaps;
}

/** An age group's price from a base cost and a coefficient, rounded to kopiykas; null unless both are set and finite. */
export function ageGroupPrice(basePolicyCost: number | null, coefficient: number | null | undefined): number | null {
  if (basePolicyCost == null || coefficient == null) return null;

  const price = Math.round(basePolicyCost * coefficient * 100) / 100;
  return Number.isFinite(price) ? price : null;
}

/** An age group's coefficient — its price relative to the base policy cost; null until both are set. */
export function ageGroupCoefficient(price: number | null | undefined, basePolicyCost: number | null | undefined): number | null {
  if (price == null || !basePolicyCost || !Number.isFinite(price) || !Number.isFinite(basePolicyCost)) return null;

  const coefficient = price / basePolicyCost;
  return Number.isFinite(coefficient) ? coefficient : null;
}
